import { useEffect, useMemo, useState, type ReactNode,
  useRef
} from "react";
import {
  ArrowDown,
  ArrowRight,
  ChevronRight,
  CalendarDays,
  Check,
  Clock3,
  Facebook,
  Instagram,
  MapPin,
  Minus,
  Phone,
  Plus,
  ShoppingBag,
  Star,
  X,
  MessageCircle,
} from "lucide-react";

import {
  submitNovaOnlineOrder,
} from "../menu/nova-online-order";

import {
  NOVA_MENU_CATEGORIES,
  NOVA_MENU_ITEMS,
  type NovaMenuCategory,
  type NovaMenuGroup,
  type NovaMenuItem,
} from "../data/nova-menu";

import {
  loadNovaWebsiteMenuCatalog,
} from "../data/nova-menu-repository";

import {
  submitNovaReservation,
} from "../operations/nova-guest-operations";

import {
  DEFAULT_NOVA_WEBSITE_CONTENT,
  loadPublishedNovaWebsiteContent,
  type NovaWebsiteContent,
} from "../content/nova-website-content";
import {
  DEFAULT_NOVA_MEDIA,
  loadPublishedNovaMedia,
  type NovaPublicMedia,
} from "../media/nova-media";

type Fulfilment = "Pickup" | "Delivery";

const CONTENT = {
  brand: {
    name: "Nova Res",
    descriptor: "Culina",
    logo: "/images/nova/logo2.png",
    darkLogo: "/images/nova/logodark.png",
    fallbackLogo: "/images/nova/novatransparent.png",
    city: DEFAULT_NOVA_WEBSITE_CONTENT.brand.city,
    phone: DEFAULT_NOVA_WEBSITE_CONTENT.brand.phone,
    whatsapp: DEFAULT_NOVA_WEBSITE_CONTENT.brand.whatsapp,
    instagram: DEFAULT_NOVA_WEBSITE_CONTENT.brand.instagram,
    address: DEFAULT_NOVA_WEBSITE_CONTENT.brand.address,
    hours: [
      [
        DEFAULT_NOVA_WEBSITE_CONTENT.brand.hours1Day,
        DEFAULT_NOVA_WEBSITE_CONTENT.brand.hours1Time,
      ],
      [
        DEFAULT_NOVA_WEBSITE_CONTENT.brand.hours2Day,
        DEFAULT_NOVA_WEBSITE_CONTENT.brand.hours2Time,
      ],
      [
        DEFAULT_NOVA_WEBSITE_CONTENT.brand.hours3Day,
        DEFAULT_NOVA_WEBSITE_CONTENT.brand.hours3Time,
      ],
    ],
  },
  chapters: [
    { key: "beginning", number: "01", label: DEFAULT_NOVA_WEBSITE_CONTENT.chapters.beginning },
    { key: "table", number: "02", label: DEFAULT_NOVA_WEBSITE_CONTENT.chapters.table },
    { key: "menu", number: "03", label: DEFAULT_NOVA_WEBSITE_CONTENT.chapters.menu },
    { key: "juice", number: "04", label: DEFAULT_NOVA_WEBSITE_CONTENT.chapters.juice },
    { key: "reviews", number: "05", label: DEFAULT_NOVA_WEBSITE_CONTENT.chapters.reviews },
    { key: "shop", number: "06", label: DEFAULT_NOVA_WEBSITE_CONTENT.chapters.shop },
    { key: "gather", number: "07", label: DEFAULT_NOVA_WEBSITE_CONTENT.chapters.gather },
    { key: "location", number: "08", label: DEFAULT_NOVA_WEBSITE_CONTENT.chapters.location },
  ],
  menuIntro:
    DEFAULT_NOVA_WEBSITE_CONTENT.menu.intro,
  reviews:
    DEFAULT_NOVA_WEBSITE_CONTENT.reviews,
} as const;

const formatPrice = (value: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 0,
  }).format(value);

function BotanicalMark({ className = "" }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 160 140"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18 117C57 87 61 50 140 18"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <path
        d="M39 94c-19 2-24-6-24-6 12-8 21-4 24 6ZM52 75c-14-7-14-17-14-17 13 1 18 8 14 17ZM65 59c-3-16 5-23 5-23 9 10 6 19-5 23ZM82 45c8-13 19-13 19-13-2 12-9 17-19 13ZM105 34c12-10 22-6 22-6-6 11-15 13-22 6ZM27 108c-3-13-12-15-12-15-3 11 2 17 12 15ZM58 83c11-10 21-7 21-7-4 11-13 15-21 7ZM92 51c14-2 19 6 19 6-10 7-18 5-19-6Z"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChapterLabel({
  number,
  eyebrow,
  title,
  copy,
  light = false,
}: {
  number: string;
  eyebrow: string;
  title: ReactNode;
  copy: string;
  light?: boolean;
}) {
  return (
    <div className="max-w-2xl">
      <div
        className={`mb-7 flex items-center gap-4 text-[10px] font-semibold uppercase tracking-[0.32em] ${
          light ? "text-[#d6bd91]" : "text-[#9d725c]"
        }`}
      >
        <span>{number}</span>
        <span className={`h-px w-12 ${light ? "bg-[#d6bd91]/60" : "bg-[#9d725c]/60"}`} />
        <span>{eyebrow}</span>
      </div>
      <h2
        className={`font-['Cormorant_Garamond'] text-5xl font-light leading-[0.9] tracking-[-0.04em] sm:text-7xl ${
          light ? "text-[#f3ead7]" : "text-[#314a3b]"
        }`}
      >
        {title}
      </h2>
      <p
        className={`mt-7 max-w-md text-sm leading-7 ${
          light ? "text-[#d9d2c2]/75" : "text-[#667067]"
        }`}
      >
        {copy}
      </p>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  required = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.2em] text-[#718076]">
        {label}
      </span>
      <input
        required={required}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full border-b border-[#b5bbae] bg-transparent px-0 py-3 text-sm text-[#314a3b] outline-none transition-colors placeholder:text-[#a2aaa0] focus:border-[#9d725c]"
      />
    </label>
  );
}

function StepIntoSectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#c9e06a]">
      {children}
    </p>
  );
}


function StepIntoLeaf({ className = "" }: { className?: string }) {
  return (
    <svg
      className={`pointer-events-none absolute opacity-60 ${className}`}
      width="102"
      height="145"
      viewBox="0 0 102 145"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M49 142C48 105 54 70 80 38M48 117C33 106 23 90 22 70M57 83C73 75 82 64 87 49M52 105C66 102 76 95 85 84M42 91C29 85 19 75 15 62M57 68C48 58 43 45 45 31"
        stroke="#c9e06a"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <path
        d="M22 70C12 67 6 59 7 51C17 51 24 57 22 70ZM87 49C82 39 84 29 91 24C96 34 94 42 87 49ZM15 62C5 61 0 54 1 46C10 46 16 52 15 62ZM85 84C79 75 81 66 89 61C94 70 92 78 85 84ZM45 31C37 24 37 15 44 9C50 16 50 24 45 31ZM48 117C37 120 29 115 27 106C37 105 44 109 48 117Z"
        fill="#c9e06a"
        fillOpacity="0.18"
        stroke="#c9e06a"
        strokeWidth="1.1"
      />
    </svg>
  );
}


function foodJournalFormatPrice(value: number) {
  return `KES ${value.toLocaleString("en-KE")}`;
}

type NovaMenuView = "all" | NovaMenuGroup;

function novaMenuItemUnitPrice(
  item: NovaMenuItem,
  selectedVariantId?: string,
) {
  if (typeof item.price === "number") {
    return item.price;
  }

  if (item.variants?.length && selectedVariantId) {
    return (
      item.variants.find(
        (variant) => variant.id === selectedVariantId,
      )?.price ?? 0
    );
  }

  return 0;
}

function novaMenuItemPriceLabel(
  item: NovaMenuItem,
) {
  if (typeof item.price === "number") {
    return foodJournalFormatPrice(item.price);
  }

  if (item.variants?.length) {
    const prices = item.variants.map(
      (variant) => variant.price,
    );

    return `From ${foodJournalFormatPrice(
      Math.min(...prices),
    )}`;
  }

  return "Price on request";
}

function FoodJournalSectionMarker({ number, label }: { number: string; label: string }) {
  return (
    <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#77796d]">
      <span className="flex h-6 w-6 items-center justify-center rounded-full border border-[#b8b8aa] text-[9px] text-[#31543c]">
        {number}
      </span>
      <span>{label}</span>
    </div>
  );
}


const NOVA_SHOP_PRODUCTS = [
  {
    id: "gift-card",
    name: "Nova Gift Card",
    description:
      "A seat at the table, wrapped for someone else.",
    price: 5000,
    category: "Gift",
    image:
      "https://images.pexels.com/photos/264507/pexels-photo-264507.jpeg?auto=compress&cs=tinysrgb&w=900",
  },
  {
    id: "house-chilli",
    name: "House Chilli",
    description:
      "A bright, slow-building table chilli inspired by the Nova kitchen.",
    price: 850,
    category: "Pantry",
    image:
      "https://images.pexels.com/photos/1435895/pexels-photo-1435895.jpeg?auto=compress&cs=tinysrgb&w=900",
  },
  {
    id: "table-notes",
    name: "Nova Table Notes",
    description:
      "A small notebook for recipes, plans and conversations worth keeping.",
    price: 1200,
    category: "Objects",
    image:
      "https://images.pexels.com/photos/261949/pexels-photo-261949.jpeg?auto=compress&cs=tinysrgb&w=900",
  },
] as const;

const FOOD_JOURNAL_PHONE = "+254 711 402 901";

export function NovaExperience() {
  const [
    publishedMedia,
    setPublishedMedia,
  ] = useState<NovaPublicMedia>(
    DEFAULT_NOVA_MEDIA,
  );

  useEffect(() => {
    let active = true;

    void loadPublishedNovaMedia()
      .then((media) => {
        if (active) {
          setPublishedMedia(
            media,
          );
        }
      });

    return () => {
      active = false;
    };
  }, []);


  const cmsEntranceClicks =
    useRef<number[]>([]);

  const handleCmsEntranceClick = () => {
    const now = Date.now();

    cmsEntranceClicks.current =
      cmsEntranceClicks.current.filter(
        (timestamp) =>
          now - timestamp <= 5000,
      );

    cmsEntranceClicks.current.push(now);

    if (
      cmsEntranceClicks.current.length >= 5
    ) {
      cmsEntranceClicks.current = [];

      window.location.href =
        "/nova-hq";
    }
  };


  const [activeChapter, setActiveChapter] = useState("beginning");
  const [sideNavTone, setSideNavTone] = useState<"light" | "dark">("light");
  const [
    menuCategories,
    setMenuCategories,
  ] = useState<
    readonly NovaMenuCategory[]
  >(NOVA_MENU_CATEGORIES);

  const [
    menuItems,
    setMenuItems,
  ] = useState<
    readonly NovaMenuItem[]
  >(NOVA_MENU_ITEMS);

  useEffect(() => {
    let cancelled = false;

    void loadNovaWebsiteMenuCatalog().then(
      (catalog) => {
        if (cancelled) {
          return;
        }

        setMenuCategories(
          catalog.categories,
        );

        setMenuItems(
          catalog.items,
        );

        if (import.meta.env.DEV) {
          console.info(
            `[Nova Web] catalog=${catalog.source} categories=${catalog.categories.length} items=${catalog.items.length}`,
          );
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, []);

  const categoryById =
    useMemo(
      () =>
        new Map(
          menuCategories.map(
            (category) => [
              category.id,
              category,
            ],
          ),
        ),
      [menuCategories],
    );

  const [websiteContent, setWebsiteContent] =
    useState<NovaWebsiteContent>(
      DEFAULT_NOVA_WEBSITE_CONTENT,
    );

  const [
    websiteContentReady,
    setWebsiteContentReady,
  ] = useState(false);

  useEffect(() => {
    let active = true;

    void loadPublishedNovaWebsiteContent()
      .then((content) => {
        if (active) {
          setWebsiteContent(content);
          setWebsiteContentReady(true);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const [activeMenuView, setActiveMenuView] =
    useState<NovaMenuView>("all");
  const [activeCategory, setActiveCategory] =
    useState<string>("all");
  const [selectedVariants, setSelectedVariants] =
    useState<Record<string, string>>({});
  const [showFullMenu, setShowFullMenu] =
    useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [cartOpen, setCartOpen] = useState(false);
  const [fulfilment, setFulfilment] = useState<Fulfilment>("Pickup");
  const [checkoutSent, setCheckoutSent] = useState(false);
  const [reservationSent, setReservationSent] = useState(false);
  const [reservationBusy, setReservationBusy] = useState(false);
  const [reservationError, setReservationError] = useState<string | null>(null);
  const [reservation, setReservation] = useState({
    name: "",
    email: "",
    date: "",
    time: "",
    guests: "2",
    note: "",
  });
  const [customer, setCustomer] = useState({ name: "", phone: "", address: "" });

  const visibleCategories = useMemo(
    () =>
      menuCategories.filter(
        (category) =>
          activeMenuView === "all" ||
          category.group === activeMenuView,
      ).sort(
        (a, b) => a.sortOrder - b.sortOrder,
      ),
    [
      activeMenuView,
      menuCategories,
    ],
  );

  const items = useMemo(
    () =>
      menuItems.filter((item) => {
        if (!item.available) {
          return false;
        }

        const category = categoryById.get(
          item.categoryId,
        );

        const matchesView =
          activeMenuView === "all" ||
          category?.group === activeMenuView;

        const matchesCategory =
          activeCategory === "all" ||
          item.categoryId === activeCategory;

        return matchesView && matchesCategory;
      }).sort(
        (a, b) => {
          const categoryA =
            categoryById.get(a.categoryId)
              ?.sortOrder ?? 0;

          const categoryB =
            categoryById.get(b.categoryId)
              ?.sortOrder ?? 0;

          if (categoryA !== categoryB) {
            return categoryA - categoryB;
          }

          return a.sortOrder - b.sortOrder;
        },
      ),
    [
      activeMenuView,
      activeCategory,
      menuItems,
      categoryById,
    ],
  );

  const displayedItems = useMemo(
    () => {
      if (
        activeCategory !== "all" ||
        showFullMenu
      ) {
        return items;
      }

      return items.slice(0, 12);
    },
    [
      items,
      activeCategory,
      showFullMenu,
    ],
  );

  const activeCategoryLabel =
    activeCategory === "all"
      ? activeMenuView === "food"
        ? "All food"
        : activeMenuView === "drinks"
          ? "All drinks"
          : "A taste of Nova"
      : categoryById.get(
          activeCategory,
        )?.label ?? "Nova menu";

  const menuIsTruncated =
    activeCategory === "all" &&
    !showFullMenu &&
    displayedItems.length < items.length;

  const cartItems = menuItems.filter(
    (item) =>
      (quantities[item.id] ?? 0) > 0,
  );

  const cartCount = cartItems.reduce(
    (sum, item) =>
      sum + (quantities[item.id] ?? 0),
    0,
  );

  const cartTotal = cartItems.reduce(
    (sum, item) =>
      sum +
      novaMenuItemUnitPrice(
        item,
        selectedVariants[item.id],
      ) *
        (quantities[item.id] ?? 0),
    0,
  );

  useEffect(() => {
    const sectionTones: Record<string, "light" | "dark"> = {
      beginning: "light",
      table: "dark",
      menu: "light",
      juice: "light",
      reviews: "dark",
      shop: "dark",
      gather: "light",
      location: "dark",
    };

    const chapterKeys = CONTENT.chapters.map(
      (chapter) => chapter.key,
    );

    const updateActiveChapter = () => {
      const viewportAnchor = window.innerHeight * 0.52;

      let currentChapter = chapterKeys[0];

      for (const chapter of chapterKeys) {
        const element = document.getElementById(chapter);

        if (!element) continue;

        const rect = element.getBoundingClientRect();

        if (rect.top <= viewportAnchor) {
          currentChapter = chapter;
        }
      }

      setActiveChapter((current) =>
        current === currentChapter
          ? current
          : currentChapter,
      );

      const nextTone =
        sectionTones[currentChapter] ?? "dark";

      setSideNavTone((current) =>
        current === nextTone
          ? current
          : nextTone,
      );
    };

    updateActiveChapter();

    window.addEventListener(
      "scroll",
      updateActiveChapter,
      { passive: true },
    );

    window.addEventListener(
      "resize",
      updateActiveChapter,
    );

    return () => {
      window.removeEventListener(
        "scroll",
        updateActiveChapter,
      );

      window.removeEventListener(
        "resize",
        updateActiveChapter,
      );
    };
  }, []);

  const changeMenuView = (
    view: NovaMenuView,
  ) => {
    setActiveMenuView(view);
    setActiveCategory("all");
    setShowFullMenu(false);
  };

  const chooseMenuCategory = (
    categoryId: string,
  ) => {
    setActiveCategory(categoryId);
    setShowFullMenu(false);
  };

  const chooseVariant = (
    itemId: string,
    variantId: string,
  ) => {
    setSelectedVariants((current) => ({
      ...current,
      [itemId]: variantId,
    }));
  };

  const jumpTo = (chapter: string) => {
    setActiveChapter(chapter);
    document.getElementById(chapter)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const changeQuantity = (id: string, delta: number) => {
    setQuantities((current) => {
      const next = Math.max(0, (current[id] ?? 0) + delta);
      return { ...current, [id]: next };
    });
  };

  const updateReservation = (key: keyof typeof reservation, value: string) =>
    setReservation((current) => ({ ...current, [key]: value }));

  const handleReservationSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();

    if (reservationBusy) {
      return;
    }

    setReservationBusy(true);
    setReservationError(null);

    const result =
      await submitNovaReservation(
        reservation,
      );

    setReservationBusy(false);

    if (!result.ok) {
      setReservationError(
        result.error ||
          "We could not send your reservation request.",
      );

      return;
    }

    setReservationSent(true);
  };

  if (!websiteContentReady) {
    return (
      <div
        className="min-h-screen bg-[#18271e]"
        aria-busy="true"
        aria-label="Loading Nova Res Culina"
      />
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#eee9dc] font-['DM_Sans'] text-[#314a3b] selection:bg-[#c79b7c] selection:text-[#f8f3e8]">
      <style>{`
        @keyframes nora-rise { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes nora-drift { 0%, 100% { transform: translateY(0) rotate(-2deg); } 50% { transform: translateY(-8px) rotate(1deg); } }
        .nora-rise { animation: nora-rise 900ms cubic-bezier(.22,.8,.24,1) both; }
        .nora-drift { animation: nora-drift 7s ease-in-out infinite; }
        .nora-noise:before { content: ""; position: fixed; inset: 0; pointer-events: none; z-index: 40; opacity: .035; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.72' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.8'/%3E%3C/svg%3E"); }
        .nora-display { font-family: Georgia, 'Times New Roman', serif; }
        .nora-sans { font-family: 'Trebuchet MS', ui-sans-serif, sans-serif; }
        .nora-rule { height: 1px; background: #c8c7bb; }
        .nora-paper { background-image: linear-gradient(rgba(38,56,44,.025) 1px, transparent 1px); background-size: 100% 5px; }
        .nora-img { filter: saturate(.83) contrast(.94); }
        html { scroll-behavior: smooth; }
      `}</style>
      <div className="nora-noise">
        <header className="fixed inset-x-0 top-0 z-30 flex items-center justify-between px-5 py-5 mix-blend-normal sm:px-10 sm:py-7">
          <button
            type="button"
            onClick={() => jumpTo("beginning")}
            className="flex items-center text-left"
            aria-label="Return to the beginning"
          >
            <img
              src={
                sideNavTone === "light"
                  ? publishedMedia.logoDark
                  : publishedMedia.logoLight
              }
              onError={(event) => {
                event.currentTarget.onerror =
                  null;

                event.currentTarget.src =
                  publishedMedia.logoFallback;
              }}
              alt="Nova Res Culina"
              className="h-20 w-auto max-w-[165px] object-contain sm:h-24 sm:max-w-[205px] lg:h-28 lg:max-w-[235px]"
            />
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setCartOpen(true)}
              className="relative flex h-11 items-center gap-2 border border-[#314a3b]/20 bg-[#eee9dc]/80 px-3 text-[10px] font-semibold uppercase tracking-[0.16em] backdrop-blur-sm transition-colors hover:bg-[#314a3b] hover:text-[#f3ead7]"
              aria-label={`Open order, ${cartCount} items`}
            >
              <ShoppingBag size={15} strokeWidth={1.5} />
              <span className="hidden sm:inline">Order</span>
              {cartCount > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#9d725c] px-1 text-[9px] text-[#f7f1e4]">
                  {cartCount}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => jumpTo("gather")}
              className="hidden h-11 bg-[#314a3b] px-5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#f3ead7] transition-colors hover:bg-[#9d725c] sm:block"
            >
              Find a table
            </button>
          </div>
        </header>

        <aside className="fixed bottom-8 left-5 z-20 hidden w-[118px] xl:block">
          <div
            className={`mb-3 text-[7px] font-semibold uppercase leading-[1.55] tracking-[0.20em] transition-colors duration-500 ${
              sideNavTone === "light"
                ? "text-[#f3ead7]/65"
                : "text-[#718076]"
            }`}
          >
            Around the Nova table
          </div>
          <nav className="space-y-2" aria-label="Story chapters">
            {CONTENT.chapters.map((chapter) => {
              const chapterLabel =
                websiteContent.chapters[
                  chapter.key as keyof typeof websiteContent.chapters
                ] ?? chapter.label;

              return (
              <button
                key={chapter.key}
                type="button"
                onClick={() => jumpTo(chapter.key)}
                className={`group flex w-full items-start gap-2 text-left text-[8px] uppercase leading-[1.35] tracking-[0.10em] transition-colors duration-500 ${
                  activeChapter === chapter.key
                    ? sideNavTone === "light"
                      ? "text-[#e5ba76]"
                      : "text-[#9d725c]"
                    : sideNavTone === "light"
                      ? "text-[#f3ead7]/60 hover:text-[#f3ead7]"
                      : "text-[#718076] hover:text-[#314a3b]"
                }`}
              >
                <span
                  className={`h-px transition-all duration-500 ${
                    activeChapter === chapter.key
                      ? sideNavTone === "light"
                        ? "w-6 bg-[#e5ba76]"
                        : "w-6 bg-[#9d725c]"
                      : sideNavTone === "light"
                        ? "w-3 bg-[#f3ead7]/35 group-hover:w-5"
                        : "w-3 bg-[#afb5a9] group-hover:w-5"
                  }`}
                />
                {chapter.number} {chapterLabel}
              </button>
                  );
            })}
          </nav>
        </aside>

        <main>
      <section
        id="beginning"
        className="relative flex min-h-[680px] items-end overflow-hidden bg-[#18271e] pt-20 text-[#f3efe2] md:min-h-[720px] lg:min-h-[760px]"
      >
        <img
          src={publishedMedia.heroBackground}
          alt={websiteContent.hero.imageAlt}
          className="absolute inset-0 h-full w-full object-cover opacity-[0.76]"
        />

        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(18,34,24,.76),rgba(18,34,24,.10)_70%,rgba(18,34,24,.32)),linear-gradient(0deg,rgba(18,34,24,.82),transparent_52%)]" />

        <StepIntoLeaf className="right-[-10px] top-40 scale-125 nora-drift md:right-20" />
        <StepIntoLeaf className="bottom-20 left-[-30px] rotate-[-35deg] scale-110 opacity-40" />

        <div className="relative z-10 mx-auto w-full max-w-[1280px] px-6 pb-12 md:px-14 md:pb-16 lg:px-20">
          <div className="max-w-[760px] nora-rise">
            <StepIntoSectionLabel>
              {websiteContent.hero.sectionLabel}
            </StepIntoSectionLabel>

            <h1 className="nora-display max-w-[760px] text-[clamp(4.3rem,9vw,8.7rem)] leading-[.78] tracking-[-.065em] text-[#f3efe2]">
              {websiteContent.hero.titleLine1}
              <br />
              <i className="text-[#c9e06a]">
                {websiteContent.hero.titleAccent}
              </i>{" "}
              {websiteContent.hero.titleLine3}
            </h1>

            <p className="mt-6 max-w-md text-[15px] leading-6 text-[#e5ead8]/80 md:text-base">
              {websiteContent.hero.copy}
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <button
                onClick={() => jumpTo("table")}
                className="flex items-center gap-3 rounded-full bg-[#c9e06a] px-6 py-3 text-xs font-bold uppercase tracking-[0.16em] text-[#18271e] transition-transform hover:scale-[1.03]"
              >
                {websiteContent.hero.primaryCta}
                <ArrowRight size={16} />
              </button>

              <button
                onClick={() => jumpTo("menu")}
                className="rounded-full border border-[#f3efe2]/50 px-6 py-3 text-xs uppercase tracking-[0.16em] hover:border-[#c9e06a] hover:text-[#c9e06a]"
              >
                {websiteContent.hero.secondaryCta}
              </button>
            </div>
          </div>

          <div className="mt-10 flex items-center gap-3 text-[8px] uppercase tracking-[0.18em] text-[#e5ead8]/60">
            <ArrowDown
              size={16}
              className="text-[#c9e06a]"
            />
            {websiteContent.hero.scrollHint}
          </div>
        </div>
      </section>

      <section
        id="table"
        className="relative flex min-h-[100svh] items-center overflow-hidden bg-[#dce6bd] px-6 py-20 md:px-14 lg:px-20"
      >
        <StepIntoLeaf className="right-[-20px] top-8 scale-150 rotate-12" />

        <div className="mx-auto grid max-w-[1280px] items-end gap-10 md:grid-cols-[.8fr_1.2fr]">
          <div>
            <div className="text-[#9d5b43] [&_*]:!text-[#9d5b43]">
              <StepIntoSectionLabel>
                {websiteContent.table.sectionLabel}
              </StepIntoSectionLabel>
            </div>

            <h2 className="nora-display max-w-md text-5xl leading-[.9] tracking-[-.05em] md:text-7xl">
              {websiteContent.table.titleLine1}
              <br />
              <i>{websiteContent.table.titleLine2}</i>
            </h2>

            <p className="mt-7 max-w-sm text-sm leading-6 text-[#344534]">
              {websiteContent.table.copy}
            </p>

            <button
              onClick={() => jumpTo("gather")}
              className="mt-8 flex items-center gap-2 border-b border-[#24372a] pb-2 text-xs font-bold uppercase tracking-[0.18em]"
            >
              {websiteContent.table.cta}
              <ChevronRight size={15} />
            </button>
          </div>

          <div className="relative h-[340px] overflow-hidden rounded-[45%_45%_4%_4%] bg-[#bfcc9d] md:h-[440px]">
            <img
              src={publishedMedia.tableImage}
              alt={websiteContent.table.imageAlt}
              className="h-full w-full object-cover mix-blend-multiply opacity-75"
            />

            <div className="absolute bottom-5 left-5 rounded-full bg-[#f3efe2]/90 px-4 py-3 text-[10px] uppercase tracking-[0.16em]">
              {websiteContent.table.imageBadge}
            </div>
          </div>
        </div>
      </section>

      <section id="menu" className="bg-[#26382c] text-[#f5f3eb]">
        <div className="mx-auto max-w-[1280px] px-5 py-20 sm:px-8 lg:px-10 lg:py-28">
          <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
            <div>
              <FoodJournalSectionMarker
                number={websiteContent.menu.sectionNumber}
                label={websiteContent.menu.marker}
              />
              <h2 className="nora-display mt-10 text-6xl leading-[.85] tracking-[-.06em] sm:text-8xl">
                {websiteContent.menu.titleLine1}
                <br />
                <em className="text-[#d47b5a]">
                  {websiteContent.menu.titleLine2}
                </em>
              </h2>
            </div>

            <div className="max-w-xs text-sm leading-6 text-[#b8c2b5]">
              {websiteContent.menu.intro}
            </div>
          </div>

          <div
            className="mt-10 flex gap-1.5 overflow-x-auto border-y border-[#536459] py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            role="tablist"
            aria-label="Food and drinks"
          >
            {(
              [
                ["all", websiteContent.menu.allTab],
                ["food", websiteContent.menu.foodTab],
                ["drinks", websiteContent.menu.drinksTab],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={
                  activeMenuView === value
                }
                onClick={() =>
                  changeMenuView(value)
                }
                className={`px-5 py-2 font-mono text-[10px] uppercase tracking-[.18em] transition-colors ${
                  activeMenuView === value
                    ? "bg-[#f5f3eb] text-[#26382c]"
                    : "text-[#c3cfc2] hover:bg-[#3c5142]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-4 pt-5 font-mono text-[8px] uppercase tracking-[.16em] text-[#7f9182]">
            <span>
              {visibleCategories.length} categories
            </span>

            <span className="hidden sm:inline">
              {websiteContent.menu.categoryHint}
            </span>
          </div>

          <div
            className="flex gap-1.5 overflow-x-auto border-b border-[#536459] py-2.5 scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            role="tablist"
            aria-label="Menu categories"
          >
            {visibleCategories.map(
              (category) => (
                <button
                  key={category.id}
                  type="button"
                  role="tab"
                  aria-selected={
                    activeCategory ===
                    category.id
                  }
                  onClick={() =>
                    chooseMenuCategory(
                      category.id,
                    )
                  }
                  className={`shrink-0 px-4 py-2 font-mono text-[10px] uppercase tracking-[.16em] transition-colors ${
                    activeCategory ===
                    category.id
                      ? "bg-[#d47b5a] text-[#26382c]"
                      : "text-[#c3cfc2] hover:bg-[#3c5142]"
                  }`}
                >
                  {category.label}
                </button>
              ),
            )}
          </div>

          <div className="mt-10 flex flex-col justify-between gap-4 border-b border-[#536459] pb-5 sm:flex-row sm:items-end">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[.18em] text-[#7f9182]">
                {websiteContent.menu.currentlyBrowsing}
              </p>

              <h3 className="nora-display mt-1.5 text-2xl leading-none text-[#f5f3eb] sm:text-3xl">
                {activeCategoryLabel}
              </h3>
            </div>

            <div className="font-mono text-[9px] uppercase tracking-[.16em] text-[#9caa9d]">
              {activeCategory === "all" &&
              !showFullMenu
                ? `${displayedItems.length} of ${items.length} items`
                : `${items.length} ${
                    items.length === 1
                      ? "item"
                      : "items"
                  }`}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-x-10 md:grid-cols-2">
            {displayedItems.map((item, index) => {
              const category =
                categoryById.get(
          item.categoryId,
        );

              const selectedVariantId =
                selectedVariants[item.id];

              const requiresVariant =
                Boolean(
                  item.variants?.length,
                );

              const canOrder =
                item.orderingEnabled &&
                (!requiresVariant ||
                  Boolean(
                    selectedVariantId,
                  ));

              return (
                <article
                  key={item.id}
                  className={`group grid grid-cols-[76px_1fr] gap-4 border-b border-[#536459] py-5 sm:grid-cols-[84px_1fr] ${
                    index % 2 === 1
                      ? "md:translate-y-6"
                      : ""
                  }`}
                >
                  <div className="relative h-[92px] overflow-hidden bg-[#31473a] sm:h-[100px]">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="nora-img h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center border border-[#536459] px-3 text-center">
                        <span className="nora-display text-2xl italic text-[#d47b5a]">
                          Nova
                        </span>
                        <span className="mt-2 font-mono text-[8px] uppercase tracking-[.15em] text-[#819184]">
                          {websiteContent.menu.photographyLine1}
                          <br />
                          {websiteContent.menu.photographyLine2}
                        </span>
                      </div>
                    )}

                    <span className="absolute left-2 top-2 font-mono text-[8px] uppercase tracking-[.12em] text-[#f5f3eb]/80">
                      {String(
                        index + 1,
                      ).padStart(2, "0")}
                    </span>
                  </div>

                  <div className="flex min-w-0 flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="nora-display text-xl leading-none sm:text-2xl">
                          {item.name}
                        </h3>

                        <span className="shrink-0 font-mono text-[9px] text-[#d47b5a]">
                          {novaMenuItemPriceLabel(
                            item,
                          )}
                        </span>
                      </div>

                      {item.description && (
                        <p className="mt-2 max-w-[320px] text-[12px] leading-[1.45] text-[#b8c2b5]">
                          {item.description}
                        </p>
                      )}

                      {item.variants?.length ? (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {item.variants.map(
                            (variant) => (
                              <button
                                key={
                                  variant.id
                                }
                                type="button"
                                onClick={() =>
                                  chooseVariant(
                                    item.id,
                                    variant.id,
                                  )
                                }
                                className={`border px-3 py-2 font-mono text-[9px] uppercase tracking-[.12em] transition-colors ${
                                  selectedVariantId ===
                                  variant.id
                                    ? "border-[#d47b5a] bg-[#d47b5a] text-[#26382c]"
                                    : "border-[#68786c] text-[#c3cfc2] hover:border-[#d47b5a]"
                                }`}
                              >
                                {
                                  variant.label
                                }{" "}
                                /{" "}
                                {foodJournalFormatPrice(
                                  variant.price,
                                )}
                              </button>
                            ),
                          )}
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-4">
                      <span className="font-mono text-[9px] uppercase tracking-[.14em] text-[#7f9182]">
                        {item.featured
                          ? websiteContent.menu.favouriteLabel
                          : category?.label ??
                            "Nova menu"}
                      </span>

                      {item.orderingEnabled ? (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              changeQuantity(
                                item.id,
                                -1,
                              )
                            }
                            disabled={
                              (quantities[
                                item.id
                              ] ?? 0) === 0
                            }
                            aria-label={`Remove one ${item.name}`}
                            className="flex h-7 w-7 items-center justify-center border border-[#68786c] text-[#d7ddd1] hover:border-[#d47b5a] disabled:cursor-not-allowed disabled:opacity-30"
                          >
                            <Minus size={12} />
                          </button>

                          <span className="w-4 text-center font-mono text-xs">
                            {quantities[
                              item.id
                            ] ?? 0}
                          </span>

                          <button
                            type="button"
                            onClick={() =>
                              changeQuantity(
                                item.id,
                                1,
                              )
                            }
                            disabled={!canOrder}
                            aria-label={`Add one ${item.name}`}
                            className="flex h-7 w-7 items-center justify-center border border-[#68786c] text-[#d7ddd1] hover:border-[#d47b5a] disabled:cursor-not-allowed disabled:opacity-30"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                      ) : (
                        <span className="font-mono text-[9px] uppercase tracking-[.12em] text-[#d47b5a]">
                          View only
                        </span>
                      )}
                    </div>

                    {requiresVariant &&
                      !selectedVariantId && (
                        <p className="mt-2 text-right font-mono text-[8px] uppercase tracking-[.12em] text-[#d47b5a]">
                          {websiteContent.menu.chooseSizeLabel}
                        </p>
                      )}
                  </div>
                </article>
              );
            })}
          </div>

          {activeCategory === "all" &&
            items.length > 12 && (
              <div className="mt-16 flex justify-center">
                <button
                  type="button"
                  onClick={() =>
                    setShowFullMenu(
                      (current) => !current,
                    )
                  }
                  className="group flex items-center gap-4 border border-[#68786c] px-6 py-4 font-mono text-[9px] uppercase tracking-[.18em] text-[#d47b5a] transition-colors hover:border-[#d47b5a] hover:bg-[#31473a]"
                >
                  <span>
                    {showFullMenu
                      ? websiteContent.menu.showHighlightsLabel
                      : menuIsTruncated
                        ? `${websiteContent.menu.exploreAllPrefix} ${items.length} ${websiteContent.menu.itemsLabel}`
                        : websiteContent.menu.showFullMenuLabel}
                  </span>

                  <ArrowRight
                    size={14}
                    className={`transition-transform ${
                      showFullMenu
                        ? "rotate-[-90deg]"
                        : "group-hover:translate-x-1"
                    }`}
                  />
                </button>
              </div>
            )}

          <div className="mt-14 flex flex-col items-start justify-between gap-5 border-t border-[#536459] pt-6 sm:flex-row sm:items-center">
            <p className="font-mono text-[10px] uppercase tracking-[.15em] text-[#aebbad]">
              {websiteContent.menu.footerLabel}
            </p>

            <button
              type="button"
              onClick={() =>
                setCartOpen(true)
              }
              className="group flex items-center gap-3 font-mono text-[10px] uppercase tracking-[.15em] text-[#d47b5a]"
            >
              {websiteContent.menu.reviewOrderLabel}
              <ArrowRight
                size={15}
                className="transition-transform group-hover:translate-x-1"
              />
            </button>
          </div>
        </div>
      </section>

      <section
        id="juice"
        className="flex min-h-[100svh] items-center overflow-hidden bg-[#26392c] px-6 py-20 text-[#f3efe2] sm:px-8 lg:px-10"
      >
        <div className="mx-auto grid max-w-[1280px] gap-10 md:grid-cols-[.7fr_1.3fr] md:items-end">
          <div>
            <StepIntoSectionLabel>
              {websiteContent.juice.sectionLabel}
            </StepIntoSectionLabel>

            <h2 className="nora-display text-6xl leading-[.86] tracking-[-.06em] md:text-8xl">
              {websiteContent.juice.titleLine1}
              <br />
              <i className="text-[#e5ba76]">
                {websiteContent.juice.titleLine2}
              </i>
            </h2>

            <p className="mt-7 max-w-sm text-sm leading-6 text-[#dbe4d5]/70">
              {websiteContent.juice.copy}
            </p>
          </div>

          <div className="relative h-[340px] overflow-hidden rounded-t-[160px] border border-[#9bb472]/40 md:h-[490px]">
            <img
              src={publishedMedia.juiceImage}
              alt={websiteContent.juice.imageAlt}
              className="h-full w-full object-cover opacity-80"
            />

            <div className="absolute inset-0 bg-[#243c2b]/20" />

            <div className="absolute bottom-6 left-6 text-sm text-[#f3efe2]">
              <span className="text-[#e5ba76]">
                {websiteContent.juice.offerLabel}
              </span>
              {" / "}
              {websiteContent.juice.offerCopy}
            </div>
          </div>
        </div>
      </section>

      <section
        id="reviews"
        className="flex min-h-[100svh] items-center border-y border-[#d5d3c7] bg-[#e8e9dc]"
      >
        <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-12 px-5 py-20 sm:px-8 lg:grid-cols-[.7fr_1fr] lg:px-10 lg:py-24">
          <div>
            <FoodJournalSectionMarker
              number={websiteContent.guestBook.sectionNumber}
              label={websiteContent.guestBook.marker}
            />
            <div className="mt-10 flex items-end gap-4">
              <span className="nora-display text-8xl leading-[.75] tracking-[-.08em] text-[#a94e35]">{websiteContent.guestBook.rating}</span>
              <div className="pb-1"><div className="flex gap-1 text-[#a94e35]">{[1, 2, 3, 4, 5].map((star) => <Star key={star} size={15} fill="currentColor" strokeWidth={1} />)}</div><p className="mt-2 font-mono text-[9px] uppercase tracking-[.15em] text-[#6c756d]">{websiteContent.guestBook.reviewCount}{" "}
                {websiteContent.guestBook.reviewCountLabel}</p></div>
            </div>
            <button type="button" onClick={() => setReviewsOpen((current) => !current)} className="mt-10 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[.16em] text-[#31543c] underline underline-offset-4">{reviewsOpen
                ? websiteContent.guestBook.closeLabel
                : websiteContent.guestBook.openLabel} <ArrowRight size={14} /></button>

            <a
              href="https://g.page/r/CROCCTU0swhBEBM/review"
              target="_blank"
              rel="noreferrer"
              className="mt-5 flex w-fit items-center gap-3 font-mono text-[10px] uppercase tracking-[.16em] text-[#a94e35] underline underline-offset-4"
            >
              Review Nova on Google
              <ArrowRight size={14} />
            </a>
          </div>
          <div
            className={[
              "grid gap-x-8 gap-y-10",
              reviewsOpen
                ? "sm:grid-cols-2"
                : "sm:grid-cols-2",
            ].join(" ")}
          >
            {(reviewsOpen
              ? websiteContent.reviews
              : websiteContent.reviews.slice(0, 2)
            ).map((review, index) => (
              <blockquote
                key={`${review.name}-${review.detail}-${index}`}
                className="min-w-0 border-t border-[#bfc1b4] pt-5"
              >
                <p className="nora-display text-2xl italic leading-[1.08] text-[#31543c]">
                  {review.quote}
                </p>

                <cite className="mt-5 block font-mono text-[9px] uppercase tracking-[.15em] text-[#77796d] not-italic">
                  {review.name}
                  {review.detail
                    ? ` / ${review.detail}`
                    : ""}
                </cite>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section
          id="shop"
          className="relative flex min-h-[100svh] items-center overflow-hidden bg-[#eee9dc] px-6 py-32 text-[#314a3b] sm:px-8 lg:px-10 lg:py-36 xl:pl-[180px] xl:pr-14"
        >
          <div
            className="pointer-events-none absolute -right-16 top-20 h-[360px] w-[360px] rounded-full border border-[#9d725c]/10"
            aria-hidden="true"
          />

          <BotanicalMark className="pointer-events-none absolute -right-14 top-24 h-[340px] w-[360px] rotate-[14deg] text-[#9d725c]/10" />

          <div className="relative z-10 mx-auto w-full max-w-[1280px]">
            <div className="grid gap-12 border-b border-[#b5bbae] pb-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
              <ChapterLabel
                number={websiteContent.atNova.sectionNumber}
                eyebrow={websiteContent.atNova.eyebrow}
                title={
                  <>
                    {websiteContent.atNova.titleLine1}
                    <br />
                    <span className="italic text-[#9d725c]">
                      {websiteContent.atNova.titleLine2}
                    </span>
                  </>
                }
                copy={websiteContent.atNova.copy}
              />

              <div className="max-w-xl lg:justify-self-end">
                <p className="font-['Cormorant_Garamond'] text-2xl font-light leading-[1.25] sm:text-3xl">
                  {websiteContent.atNova.lead}
                </p>

                <p className="mt-5 max-w-lg text-sm leading-7 text-[#667067]">
                  {websiteContent.atNova.body}
                </p>

                <button
                  type="button"
                  onClick={() => {
                window.location.href =
                  "/enquire";
              }}
                  className="mt-7 inline-flex items-center gap-3 border-b border-[#9d725c] pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9d725c] transition-colors hover:border-[#314a3b] hover:text-[#314a3b]"
                >
                  {websiteContent.atNova.cta}
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>

            <div className="mt-8 grid gap-4 lg:grid-cols-12">
              <article className="relative min-h-[520px] overflow-hidden bg-[#26392c] p-8 text-[#f3ead7] sm:p-10 lg:col-span-7 lg:min-h-[620px]">
                {publishedMedia.atNovaFeature && (
                  <video
                    src={publishedMedia.atNovaFeature}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 h-full w-full object-cover"
                    aria-label="Nova after hours"
                  />
                )}

                {publishedMedia.atNovaFeature && (
                  <div className="absolute inset-0 bg-[#18271e]/55" />
                )}

                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "radial-gradient(circle at 75% 20%, rgba(201,224,106,.16), transparent 32%), linear-gradient(145deg, rgba(157,114,92,.20), transparent 52%)",
                  }}
                />

                <BotanicalMark className="absolute -right-10 top-12 h-56 w-56 text-[#c9e06a]/40" />

                <div className="relative flex h-full flex-col justify-between">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-semibold uppercase tracking-[0.24em] text-[#d6bd91]">
                      {websiteContent.atNova.reelLabel}
                    </span>

                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[#f3ead7]/25 text-[9px]">
                      {websiteContent.atNova.reelNumber}
                    </span>
                  </div>

                  <div className="mt-32 max-w-xl">
                    <h3 className="font-['Cormorant_Garamond'] text-5xl font-light leading-[0.92] sm:text-6xl lg:text-7xl">
                      {websiteContent.atNova.reelTitleLine1}
                      <br />
                      <span className="italic text-[#c9e06a]">
                        {websiteContent.atNova.reelTitleLine2}
                      </span>
                    </h3>

                    <p className="mt-6 max-w-md text-sm leading-6 text-[#d9d2c2]/75">
                      {websiteContent.atNova.reelCopy}
                    </p>
                  </div>

                  <div className="mt-10 flex items-center gap-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#d6bd91]">
                    <span className="h-px w-10 bg-[#d6bd91]/50" />
                    {websiteContent.atNova.reelFooter}
                  </div>
                </div>
              </article>

              <div className="grid gap-4 lg:col-span-5">
                <article className="relative min-h-[300px] overflow-hidden bg-[#dce6bd] p-8">
                  {publishedMedia.atNovaGameNights && (
                    <>
                      <img
                        src={publishedMedia.atNovaGameNights}
                        alt={websiteContent.atNova.gameLabel}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 bg-[#18271e]/58" />
                    </>
                  )}

                  {!publishedMedia.atNovaGameNights && (
                    <StepIntoLeaf className="-right-4 bottom-0" />
                  )}

                  <div className="relative z-10 flex h-full flex-col justify-between">
                    <span
                      className={[
                        "text-[9px] font-semibold uppercase tracking-[0.24em]",
                        publishedMedia.atNovaGameNights
                          ? "text-[#d6bd91]"
                          : "text-[#718076]",
                      ].join(" ")}
                    >
                      {websiteContent.atNova.gameLabel}
                    </span>

                    <div className="mt-14">
                      <h3
                        className={[
                          "font-['Cormorant_Garamond'] text-4xl font-light leading-none sm:text-5xl",
                          publishedMedia.atNovaGameNights
                            ? "text-[#f3ead7]"
                            : "",
                        ].join(" ")}
                      >
                        {websiteContent.atNova.gameTitleLine1}
                        <br />
                        {websiteContent.atNova.gameTitleLine2}
                      </h3>

                      <p
                        className={[
                          "mt-4 max-w-sm text-sm leading-6",
                          publishedMedia.atNovaGameNights
                            ? "text-[#f3ead7]/80"
                            : "text-[#667067]",
                        ].join(" ")}
                      >
                        {websiteContent.atNova.gameCopy}
                      </p>
                    </div>
                  </div>
                </article>

                <article className="relative min-h-[300px] overflow-hidden bg-[#d7c5b4] p-8">
                  {publishedMedia.atNovaSetups && (
                    <>
                      <img
                        src={publishedMedia.atNovaSetups}
                        alt={websiteContent.atNova.celebrationLabel}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 bg-[#3f2f28]/56" />
                    </>
                  )}

                  <div className="relative z-10 flex h-full flex-col justify-between">
                    <span
                      className={[
                        "text-[9px] font-semibold uppercase tracking-[0.24em]",
                        publishedMedia.atNovaSetups
                          ? "text-[#f0d7b0]"
                          : "text-[#7f5e4d]",
                      ].join(" ")}
                    >
                      {websiteContent.atNova.celebrationLabel}
                    </span>

                    <div className="mt-14">
                      <h3
                        className={[
                          "font-['Cormorant_Garamond'] text-4xl font-light leading-none sm:text-5xl",
                          publishedMedia.atNovaSetups
                            ? "text-[#f3ead7]"
                            : "",
                        ].join(" ")}
                      >
                        {websiteContent.atNova.celebrationTitleLine1}
                        <br />
                        {websiteContent.atNova.celebrationTitleLine2}
                      </h3>

                      <p
                        className={[
                          "mt-4 max-w-sm text-sm leading-6",
                          publishedMedia.atNovaSetups
                            ? "text-[#f3ead7]/80"
                            : "text-[#5f665f]",
                        ].join(" ")}
                      >
                        {websiteContent.atNova.celebrationCopy}
                      </p>
                    </div>
                  </div>
                </article>
              </div>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <article className="relative min-h-[220px] overflow-hidden border border-[#b5bbae] p-7 sm:p-8">
                {publishedMedia.atNovaKitchen && (
                  <>
                    <img
                      src={publishedMedia.atNovaKitchen}
                      alt={websiteContent.atNova.kitchenLabel}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-[#18271e]/58" />
                  </>
                )}

                <div className="relative z-10">
                  <span
                    className={[
                      "text-[9px] font-semibold uppercase tracking-[0.22em]",
                      publishedMedia.atNovaKitchen
                        ? "text-[#d6bd91]"
                        : "text-[#9d725c]",
                    ].join(" ")}
                  >
                    {websiteContent.atNova.kitchenLabel}
                  </span>

                  <p
                    className={[
                      "mt-5 font-['Cormorant_Garamond'] text-3xl leading-none",
                      publishedMedia.atNovaKitchen
                        ? "text-[#f3ead7]"
                        : "",
                    ].join(" ")}
                  >
                    {websiteContent.atNova.kitchenCopy}
                  </p>
                </div>
              </article>

              <article className="relative min-h-[220px] overflow-hidden border border-[#b5bbae] p-7 sm:p-8">
                {publishedMedia.atNovaMoments && (
                  <>
                    <img
                      src={publishedMedia.atNovaMoments}
                      alt={websiteContent.atNova.momentsLabel}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-[#18271e]/58" />
                  </>
                )}

                <div className="relative z-10">
                  <span
                    className={[
                      "text-[9px] font-semibold uppercase tracking-[0.22em]",
                      publishedMedia.atNovaMoments
                        ? "text-[#d6bd91]"
                        : "text-[#9d725c]",
                    ].join(" ")}
                  >
                    {websiteContent.atNova.momentsLabel}
                  </span>

                  <p
                    className={[
                      "mt-5 font-['Cormorant_Garamond'] text-3xl leading-none",
                      publishedMedia.atNovaMoments
                        ? "text-[#f3ead7]"
                        : "",
                    ].join(" ")}
                  >
                    {websiteContent.atNova.momentsCopy}
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section id="gather" className="relative overflow-hidden bg-[#314a3b] px-6 py-28 text-[#f3ead7] sm:px-[12vw] sm:py-40">
            <BotanicalMark className="absolute -bottom-16 -right-24 h-[480px] w-[500px] rotate-[15deg] text-[#d6bd91]/30" />
            <div className="relative z-10 grid gap-20 lg:grid-cols-[1fr_1.15fr] lg:gap-28">
              <div>
                <ChapterLabel
                  number={websiteContent.reservation.sectionNumber}
                  eyebrow={websiteContent.reservation.eyebrow}
                  title={
                    <>
                      {websiteContent.reservation.titleLine1}
                      <br />
                      {websiteContent.reservation.titleLine2}
                    </>
                  }
                  copy={websiteContent.reservation.copy}
                  light
                />
                <div className="mt-16 space-y-7 text-sm text-[#d9d2c2]/80">
                  <div className="flex gap-4"><MapPin size={17} className="mt-0.5 shrink-0 text-[#d6bd91]" strokeWidth={1.2} /><span>{websiteContent.location.address}
                  <br />
                  {websiteContent.location.locality}</span></div>
                  <div className="flex gap-4"><Clock3 size={17} className="mt-0.5 shrink-0 text-[#d6bd91]" strokeWidth={1.2} /><span>{[
                      [
                        websiteContent.brand.hours1Day,
                        websiteContent.brand.hours1Time,
                      ],
                      [
                        websiteContent.brand.hours2Day,
                        websiteContent.brand.hours2Time,
                      ],
                      [
                        websiteContent.brand.hours3Day,
                        websiteContent.brand.hours3Time,
                      ],
                    ].map(([day, hours]) => (
                      <span className="block" key={day}>
                        <b className="mr-3 font-normal text-[#f3ead7]">
                          {day}
                        </b>
                        {hours}
                      </span>
                    ))}</span></div>
                  <div className="flex flex-wrap gap-x-5 gap-y-3 pt-2 text-[10px] font-semibold uppercase tracking-[0.15em]">
                    <a href={`tel:${websiteContent.brand.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-[#d6bd91] hover:text-[#f3ead7]"><Phone size={14} /> {websiteContent.reservation.callLabel}</a>
                    <a href={`https://wa.me/${websiteContent.brand.whatsapp.replace(/\D/g, "")}`} className="flex items-center gap-2 text-[#d6bd91] hover:text-[#f3ead7]"><MessageCircle size={14} /> {websiteContent.reservation.whatsappLabel}</a>
                  </div>
                </div>
              </div>
              <div className="bg-[#eee9dc] p-6 text-[#314a3b] sm:p-10">
                {reservationSent ? (
                  <div className="flex min-h-[430px] flex-col items-center justify-center text-center">
                    <span className="mb-7 flex h-16 w-16 items-center justify-center rounded-full border border-[#9d725c] text-[#9d725c]"><Check size={25} strokeWidth={1.4} /></span>
                    <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#9d725c]">{websiteContent.reservation.successEyebrow}</p>
                    <h3 className="font-['Cormorant_Garamond'] text-5xl leading-none">{websiteContent.reservation.successTitle}</h3>
                    <p className="mt-6 max-w-xs text-sm leading-6 text-[#667067]">
                      {websiteContent.reservation.successCopy}
                    </p>
                    <button type="button" onClick={() => setReservationSent(false)} className="mt-8 border-b border-[#9d725c] pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9d725c]">{websiteContent.reservation.successAgain}</button>
                  </div>
                ) : (
                  <form onSubmit={handleReservationSubmit} className="space-y-7">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      <button
                        type="button"
                        className="border border-[#314a3b] bg-[#314a3b] px-4 py-3 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#f3ead7]"
                      >
                        Reserve a Table
                      </button>

                      <a
                        href="/enquire?service=outside_catering"
                        className="border border-[#b5bbae] px-4 py-3 text-center text-[9px] font-semibold uppercase tracking-[0.16em] text-[#314a3b] transition-colors hover:border-[#9d725c] hover:text-[#9d725c]"
                      >
                        Outside Catering
                      </a>

                      <a
                        href="/enquire?service=birthday_cake"
                        className="border border-[#b5bbae] px-4 py-3 text-center text-[9px] font-semibold uppercase tracking-[0.16em] text-[#314a3b] transition-colors hover:border-[#9d725c] hover:text-[#9d725c]"
                      >
                        Order a Cake
                      </a>
                    </div>

                    <div className="mb-10 flex items-end justify-between gap-4 border-b border-[#b5bbae] pb-6">
                      <div><p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9d725c]">{websiteContent.reservation.formEyebrow}</p><h3 className="mt-3 font-['Cormorant_Garamond'] text-4xl leading-none">{websiteContent.reservation.formTitle}</h3></div>
                      <CalendarDays className="mb-1 text-[#9d725c]" size={28} strokeWidth={1} />
                    </div>
                    <Field label={websiteContent.reservation.nameLabel} value={reservation.name} onChange={(value) => updateReservation("name", value)} placeholder={websiteContent.reservation.namePlaceholder} required />
                    <Field label={websiteContent.reservation.emailLabel} type="email" value={reservation.email} onChange={(value) => updateReservation("email", value)} placeholder={websiteContent.reservation.emailPlaceholder} required />
                    <div className="grid gap-6 sm:grid-cols-2">
                      <Field label={websiteContent.reservation.dateLabel} type="date" value={reservation.date} onChange={(value) => updateReservation("date", value)} required />
                      <Field label={websiteContent.reservation.timeLabel} type="time" value={reservation.time} onChange={(value) => updateReservation("time", value)} required />
                    </div>
                    <label className="block"><span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.2em] text-[#718076]">{websiteContent.reservation.guestsLabel}</span><select value={reservation.guests} onChange={(event) => updateReservation("guests", event.target.value)} className="w-full border-b border-[#b5bbae] bg-transparent py-3 text-sm text-[#314a3b] outline-none"><option>2</option><option>3</option><option>4</option><option>5</option><option>6+</option></select></label>
                    <label className="block"><span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.2em] text-[#718076]">{websiteContent.reservation.noteLabel} <span className="font-normal normal-case tracking-normal">{websiteContent.reservation.optionalLabel}</span></span><textarea value={reservation.note} onChange={(event) => updateReservation("note", event.target.value)} rows={2} placeholder={websiteContent.reservation.notePlaceholder} className="w-full resize-none border-b border-[#b5bbae] bg-transparent py-3 text-sm text-[#314a3b] outline-none placeholder:text-[#a2aaa0] focus:border-[#9d725c]" /></label>
                    {reservationError && (
                      <div className="border border-[#9d725c]/30 bg-[#9d725c]/8 px-4 py-3 text-xs leading-5 text-[#d7b7a8]">
                        {reservationError}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={reservationBusy}
                      className="mt-2 flex w-full items-center justify-between bg-[#314a3b] px-5 py-4 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-[#f3ead7] transition-colors hover:bg-[#9d725c] disabled:cursor-wait disabled:opacity-60"
                    >
                      <span>
                        {reservationBusy
                          ? websiteContent.reservation.sendingLabel
                          : websiteContent.reservation.submitLabel}
                      </span>
                      <ArrowRight size={16} />
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>

      <section id="location" className="mx-auto flex min-h-[100svh] max-w-[1440px] items-center px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
        <div className="grid w-full grid-cols-1 gap-14 lg:grid-cols-[1fr_1fr] lg:gap-16">
          <div>
            <FoodJournalSectionMarker
              number={websiteContent.location.sectionNumber}
              label={websiteContent.location.marker}
            />
            <h2 className="nora-display mt-10 max-w-xl text-6xl leading-[.86] tracking-[-.06em] sm:text-8xl">
              {websiteContent.location.titleLine1}
              <br />
              <em className="text-[#a94e35]">
                {websiteContent.location.titleLine2}
              </em>
            </h2>
            <p className="nora-sans mt-10 max-w-md text-sm leading-7 text-[#657067]">
              {websiteContent.location.copy}
            </p>
            <div className="mt-12 grid grid-cols-1 gap-8 border-t border-[#c8c7bb] pt-6 sm:grid-cols-3">
              <div><MapPin size={17} className="mb-4 text-[#a94e35]" strokeWidth={1.5} /><p className="font-mono text-[10px] uppercase tracking-[.14em] leading-5">{websiteContent.location.address}
                    <br />
                    {websiteContent.location.locality}</p></div>
              <div><Clock3 size={17} className="mb-4 text-[#a94e35]" strokeWidth={1.5} /><p className="font-mono text-[10px] uppercase tracking-[.14em] leading-5">{websiteContent.location.hoursLine1}
                    <br />
                    {websiteContent.location.hoursLine2}</p></div>
              <div><Phone size={17} className="mb-4 text-[#a94e35]" strokeWidth={1.5} /><a href={`tel:${websiteContent.location.phone}`} className="font-mono text-[10px] uppercase tracking-[.14em] leading-5 underline underline-offset-4">{websiteContent.location.phone}</a></div>
            </div>
          </div>
          <a
            href={
              websiteContent.location.mapUrl ||
              "https://maps.app.goo.gl/7sTsMRxR6SpeTJrG6"
            }
            target="_blank"
            rel="noreferrer"
            aria-label={`${websiteContent.location.mapCta || "Get directions"} — ${websiteContent.location.mapNameLine1} ${websiteContent.location.mapNameLine2}`}
            className="group relative min-h-[390px] overflow-hidden bg-[#d0d4c7] outline-none transition-[transform,box-shadow] duration-500 focus-visible:ring-2 focus-visible:ring-[#a94e35] focus-visible:ring-offset-4 focus-visible:ring-offset-[#eee9dc] lg:min-h-[440px]"
          >
            <div
              className="absolute inset-0 opacity-75 transition-transform duration-700 group-hover:scale-[1.025]"
              style={{
                backgroundImage:
                  "linear-gradient(32deg, transparent 48%, #9ca99b 49%, #9ca99b 50%, transparent 51%), linear-gradient(-22deg, transparent 48%, #b0b8aa 49%, #b0b8aa 50%, transparent 51%)",
                backgroundSize:
                  "120px 100px, 150px 120px",
              }}
            />

            <div className="absolute inset-x-[-12%] top-[18%] h-px rotate-[8deg] bg-[#8f9d8d]/55" />
            <div className="absolute inset-x-[-12%] top-[68%] h-px rotate-[-12deg] bg-[#8f9d8d]/45" />
            <div className="absolute -bottom-[8%] left-[18%] h-[125%] w-px rotate-[22deg] bg-[#a8b2a3]/55" />

            <div className="absolute left-[32%] top-[31%] h-24 w-32 rotate-[-8deg] border-2 border-[#31543c] bg-[#e8e9dc] p-3 shadow-[12px_12px_0_#a94e35] transition-transform duration-500 group-hover:-translate-y-1 group-hover:rotate-[-5deg]">
              <MapPin
                className="mx-auto text-[#a94e35]"
                size={20}
              />

              <p className="mt-2 text-center font-mono text-[9px] uppercase tracking-[.12em]">
                {websiteContent.location.mapNameLine1}
                <br />
                {websiteContent.location.mapNameLine2}
              </p>
            </div>

            <span className="absolute bottom-5 left-5 max-w-[62%] font-mono text-[9px] uppercase tracking-[.15em] text-[#31543c]">
              {websiteContent.location.mapCoordinates}
            </span>

            <span className="absolute bottom-5 right-5 flex items-center gap-2 border border-[#31543c] bg-[#e8e9dc] px-4 py-3 font-mono text-[8px] font-semibold uppercase tracking-[.14em] text-[#31543c] shadow-[5px_5px_0_#a94e35] transition-all duration-300 group-hover:-translate-y-1 group-hover:bg-[#31543c] group-hover:text-[#f3ead7] group-hover:shadow-[7px_7px_0_#a94e35]">
              {websiteContent.location.mapCta ||
                "Get directions"}
              <ArrowRight
                size={12}
              />
            </span>

            <span
              className="absolute right-5 top-5 h-2 w-2 rounded-full bg-[#a94e35] shadow-[0_0_0_5px_rgba(169,78,53,.12)] transition-transform duration-500 group-hover:scale-125"
              aria-hidden="true"
            />
          </a>
        </div>
      </section>

          <footer className="bg-[#eee9dc] px-6 pb-10 pt-20 text-[#314a3b] sm:px-[8vw]">
            <div className="grid gap-12 border-b border-[#afb7aa] pb-14 md:grid-cols-[1.2fr_1fr_1fr]">
              <div>
                <button
                  type="button"
                  onClick={handleCmsEntranceClick}
                  className="block text-left"
                  aria-label="Nova Res Culina"
                >
<img
                  src={publishedMedia.logoLight}
                  onError={(event) => {
                    event.currentTarget.onerror =
                      null;

                    event.currentTarget.src =
                      publishedMedia.logoFallback;
                  }}
                  alt="Nova Res Culina"
                  className="mb-6 h-auto w-[150px] object-contain sm:w-[170px]"
                />
                </button>
                <p className="max-w-xs font-['Cormorant_Garamond'] text-3xl leading-none">
                  {websiteContent.footer.taglineLine1}
                  <br />
                  {websiteContent.footer.taglineLine2}
                </p>
              </div>
              <div>
                <p className="mb-5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
                  {websiteContent.footer.findUsLabel}
                </p>
                <p className="text-sm leading-6 text-[#667067]">
                  {websiteContent.location.address}
                  <br />
                  {websiteContent.location.locality}
                </p>
                <a
                  className="mt-4 block text-sm text-[#314a3b] underline decoration-[#9d725c]"
                  href={`tel:${websiteContent.location.phone.replace(/\s/g, "")}`}
                >
                  {websiteContent.location.phone}
                </a>
              </div>
              <div>
                <p className="mb-5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9d725c]">
                  {websiteContent.footer.keepInTouchLabel}
                </p>
                <div className="flex gap-3">
                  <a href="https://www.instagram.com/novaresculina/" target="_blank" rel="noreferrer" aria-label="Instagram" className="flex h-9 w-9 items-center justify-center border border-[#afb7aa] text-[#314a3b] hover:border-[#9d725c] hover:text-[#9d725c]"><Instagram size={15} /></a>
                  <a href={websiteContent.footer.facebookUrl} aria-label="Facebook" className="flex h-9 w-9 items-center justify-center border border-[#afb7aa] text-[#314a3b] hover:border-[#9d725c] hover:text-[#9d725c]"><Facebook size={15} /></a>
                  <a href="https://wa.me/254719275522" target="_blank" rel="noreferrer" aria-label="WhatsApp" className="flex h-9 w-9 items-center justify-center border border-[#afb7aa] text-[#314a3b] hover:border-[#9d725c] hover:text-[#9d725c]"><MessageCircle size={15} /></a>
                </div>
                <p className="mt-5 text-xs text-[#667067]">@novaresculina</p>
              </div>
            </div>
            <div className="flex flex-col justify-between gap-3 pt-6 text-[9px] uppercase tracking-[0.18em] text-[#8d988d] sm:flex-row"><span>{websiteContent.footer.copyright}</span>
              <span>{websiteContent.footer.closingLine}</span></div>
          </footer>
        </main>

        {cartOpen && (
          <div className="fixed inset-0 z-50 flex justify-end bg-[#314a3b]/40 backdrop-blur-[2px]" role="dialog" aria-modal="true" aria-label="Your order">
            <button type="button" aria-label="Close order panel" onClick={() => setCartOpen(false)} className="absolute inset-0 cursor-default" />
            <div className="relative flex h-full w-full max-w-md flex-col bg-[#eee9dc] p-6 text-[#314a3b] shadow-2xl sm:p-9">
              <div className="flex items-start justify-between border-b border-[#b5bbae] pb-6"><div><p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9d725c]">Your order</p><h2 className="mt-3 font-['Cormorant_Garamond'] text-4xl leading-none">For the table.</h2></div><button type="button" onClick={() => setCartOpen(false)} className="p-2 text-[#667067] hover:text-[#314a3b]" aria-label="Close order"><X size={20} /></button></div>
              {checkoutSent ? (
                <div className="flex flex-1 flex-col items-center justify-center text-center"><span className="mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-[#9d725c] text-[#9d725c]"><Check size={23} /></span><h3 className="font-['Cormorant_Garamond'] text-4xl">We&apos;re on it.</h3><p className="mt-4 max-w-xs text-sm leading-6 text-[#667067]">Your order is safely recorded with Nova. We&apos;ll prepare it for your selected pickup or delivery option.</p><p className="mt-4 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#9d725c]">{window.sessionStorage.getItem("nova-last-order-code") ? `Order ${window.sessionStorage.getItem("nova-last-order-code")}` : "Order confirmed"}</p><button type="button" onClick={() => { setCheckoutSent(false); setCartOpen(false); }} className="mt-8 border-b border-[#9d725c] pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9d725c]">Back to the story</button></div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto py-7">
                    {cartItems.length === 0 ? (
                      <div className="flex min-h-[280px] flex-col items-center justify-center px-4 text-center">
                        <ShoppingBag
                          className="mb-5 text-[#9d725c]"
                          size={27}
                          strokeWidth={1}
                        />

                        <p className="font-['Cormorant_Garamond'] text-3xl leading-none">
                          The middle is waiting.
                        </p>

                        <p className="mt-3 max-w-xs text-xs leading-5 text-[#667067]">
                          Start with the Nova menu, choose what you would
                          like, then return here to complete your order
                          request.
                        </p>

                        <button
                          type="button"
                          onClick={() => {
                            setCartOpen(false);

                            requestAnimationFrame(() => {
                              jumpTo("menu");
                            });
                          }}
                          className="mt-7 flex items-center gap-3 border-b border-[#9d725c] pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9d725c] transition-colors hover:border-[#314a3b] hover:text-[#314a3b]"
                        >
                          Browse the menu
                          <ArrowRight size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-5">
                        {cartItems.map((item) => {
                          const quantity =
                            quantities[item.id] ?? 0;

                          const variant =
                            item.variants?.find(
                              (candidate) =>
                                candidate.id ===
                                selectedVariants[
                                  item.id
                                ],
                            );

                          const unitPrice =
                            novaMenuItemUnitPrice(
                              item,
                              selectedVariants[
                                item.id
                              ],
                            );

                          return (
                            <div
                              className="flex gap-4"
                              key={item.id}
                            >
                              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden bg-[#dfe3d7]">
                                {item.image ? (
                                  <img
                                    src={item.image}
                                    alt=""
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <span className="font-['Cormorant_Garamond'] text-lg italic text-[#9d725c]">
                                    Nova
                                  </span>
                                )}
                              </div>

                              <div className="min-w-0 flex-1">
                                <div className="flex justify-between gap-3">
                                  <div>
                                    <p className="font-['Cormorant_Garamond'] text-xl leading-none">
                                      {item.name}
                                    </p>

                                    {variant && (
                                      <p className="mt-1 text-[9px] uppercase tracking-[.14em] text-[#718076]">
                                        {
                                          variant.label
                                        }
                                      </p>
                                    )}
                                  </div>

                                  <span className="text-xs text-[#9d725c]">
                                    {foodJournalFormatPrice(
                                      unitPrice *
                                        quantity,
                                    )}
                                  </span>
                                </div>

                                <div className="mt-3 flex items-center gap-3 text-xs">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      changeQuantity(
                                        item.id,
                                        -1,
                                      )
                                    }
                                    className="flex h-6 w-6 items-center justify-center border border-[#afb7aa]"
                                    aria-label={`Remove one ${item.name}`}
                                  >
                                    <Minus
                                      size={11}
                                    />
                                  </button>

                                  <span>
                                    {quantity}
                                  </span>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      changeQuantity(
                                        item.id,
                                        1,
                                      )
                                    }
                                    className="flex h-6 w-6 items-center justify-center bg-[#314a3b] text-[#f3ead7]"
                                    aria-label={`Add one ${item.name}`}
                                  >
                                    <Plus
                                      size={11}
                                    />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {cartItems.length > 0 && <div className="mt-10 border-t border-[#b5bbae] pt-6"><p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#718076]">How would you like it?</p><div className="grid grid-cols-2 gap-2">{(["Pickup", "Delivery"] as Fulfilment[]).map((option) => <button key={option} type="button" onClick={() => setFulfilment(option)} className={`border px-3 py-3 text-left text-xs transition-colors ${fulfilment === option ? "border-[#314a3b] bg-[#314a3b] text-[#f3ead7]" : "border-[#afb7aa] text-[#667067]"}`}>{option}<span className="mt-1 block text-[10px] opacity-70">{option === "Pickup" ? "Ready in 25 min" : "Within Nairobi"}</span></button>)}</div><div className="mt-7 space-y-6"><Field label="Your name" value={customer.name} onChange={(value) => setCustomer((current) => ({ ...current, name: value }))} placeholder="Name" required /><Field label="Phone" type="tel" value={customer.phone} onChange={(value) => setCustomer((current) => ({ ...current, phone: value }))} placeholder="+254" required />{fulfilment === "Delivery" && <Field label="Delivery address" value={customer.address} onChange={(value) => setCustomer((current) => ({ ...current, address: value }))} placeholder="Street, building, area" required />}</div></div>}
                  </div>
                  <div className="border-t border-[#b5bbae] pt-6"><div className="mb-5 flex items-center justify-between"><span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#718076]">Estimated total</span><span className="font-['Cormorant_Garamond'] text-3xl">{foodJournalFormatPrice(cartTotal)}</span></div><button type="button" disabled={!cartItems.length || !customer.name || !customer.phone || (fulfilment === "Delivery" && !customer.address)} onClick={async () => {
  try {
    const confirmation =
      await submitNovaOnlineOrder({
        serviceMode:
          fulfilment === "Delivery"
            ? "delivery"
            : "pickup",

        guestName:
          customer.name,

        guestPhone:
          customer.phone,

        deliveryAddress:
          fulfilment === "Delivery"
            ? customer.address
            : null,

        lines:
          cartItems.map(
            (item) => ({
              itemId:
                item.id,

              variantId:
                selectedVariants[
                  item.id
                ] ?? null,

              quantity:
                quantities[
                  item.id
                ] ?? 0,
            }),
          ),
      });

    window.sessionStorage.setItem(
      "nova-last-order-code",
      confirmation.orderCode,
    );

    //
    // Do NOT clear anything until
    // the backend has confirmed it.
    //
    setQuantities({});

    setCheckoutSent(true);
  } catch (error) {
    window.alert(
      error instanceof Error
        ? `${error.message}\n\nYour basket has been kept. Please try again.`
        : "We could not place your order. Your basket has been kept.",
    );
  }
}} className="flex w-full items-center justify-between bg-[#9d725c] px-5 py-4 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-[#f7f1e4] transition-colors hover:bg-[#314a3b] disabled:cursor-not-allowed disabled:opacity-40"><span>Place order</span><ArrowRight size={16} /></button></div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}